/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { Result } from "../../network/http-client"
import type { ViewModel } from "../../viewmodel/viewmodel"

import type { OrganizationService } from "./organization-service"
import type { OrganizationStore } from "./organization-store"
import type { Organization, OrganizationDetail, SelectedOrgId } from "./organization-types"

export class OrganizationViewModel implements ViewModel {
    name: string = this.constructor.name

    // The fetch `ensureOrganizations` is waiting on, so parallel callers share one request.
    private pendingOrganizations: Promise<void> | null = null

    constructor(
        private readonly store: OrganizationStore,
        private readonly service: OrganizationService
    ) {}

    // Getters
    get organizations(): Organization[] {
        return this.store.organizationsArray
    }

    get organizationIds(): number[] {
        return this.store.organizationsIds
    }

    get hasOrganizations(): boolean {
        return this.store.organizationsArray.length > 0
    }

    get organizationsLoaded(): boolean {
        return this.store.organizationsLoaded
    }

    get selectedOrgId(): SelectedOrgId {
        return this.store.selectedOrgId
    }

    get selectedOrganizationDetail(): Organization | null {
        return this.store.selectedOrganization
    }

    setSelectedOrgId(orgId: SelectedOrgId): void {
        this.store.setSelectedOrgId(orgId)
    }

    get hasMultipleOrganizations(): boolean {
        return this.store.organizationsArray.length > 1
    }

    get selectedOrgName(): string {
        return this.store.selectedOrgName
    }

    // Service calls
    async getOrganizations(): Promise<void> {
        await this.service.getOrganizations()
    }

    /**
     * Loads organizations only when the store does not have a completed fetch.
     *
     * Callers that arrive while a request is in flight wait for it instead of
     * starting their own, so a quick sequence of routes fetches once. A failed
     * request leaves `organizationsLoaded` false, so a later call can retry the
     * fetch.
     */
    async ensureOrganizations(): Promise<void> {
        if (this.store.organizationsLoaded) return

        this.pendingOrganizations ??= this.getOrganizations().finally(() => {
            this.pendingOrganizations = null
        })

        await this.pendingOrganizations
    }

    async createOrganization(
        name: string,
        artifactoryUsername: string,
        artifactoryPassword: string
    ): Promise<Result<Organization>> {
        return await this.service.createOrganization(name, artifactoryUsername, artifactoryPassword)
    }

    async updateOrganization(
        orgId: number,
        name: string,
        artifactoryUsername: string,
        artifactoryPassword: string
    ): Promise<void> {
        await this.service.updateOrganization(orgId, name, artifactoryUsername, artifactoryPassword)
    }

    async getOrganization(orgId: number): Promise<Result<OrganizationDetail>> {
        return await this.service.getOrganization(orgId)
    }

    async deleteOrganization(orgId: number): Promise<void> {
        await this.service.deleteOrganization(orgId)
    }
}
